import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Loader2, X, Upload, Image, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompanyProfile() {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [nip, setNip] = useState('');
  const [regon, setRegon] = useState('');
  const [address, setAddress] = useState('');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [logoUrl, setLogoUrl] = useState('');

  const isCompany = user?.user_type === 'company';

  useEffect(() => {
    if (!isAuthenticated || !isCompany) {
      navigate('/login');
      return;
    }
    fetchCompany();
  }, [token, isAuthenticated, isCompany, navigate]);

  async function fetchCompany() {
    try {
      const res = await fetch(`${API}/my-company`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setName(data.name || '');
        setDescription(data.description || '');
        setLocation(data.location || '');
        setPhone(data.phone || '');
        setWebsite(data.website || '');
        setNip(data.nip || '');
        setRegon(data.regon || '');
        setAddress(data.address || '');
        setInvoiceEmail(data.invoice_email || '');
        setCompanyName(data.company_name || data.name || '');
        setServices(data.services || []);
        setGalleryImages(data.gallery_images || []);
        setLogoUrl(data.logo_url || '');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleAddService() {
    if (newService.trim() && !services.includes(newService.trim())) {
      setServices([...services, newService.trim()]);
      setNewService('');
    }
  }

  function handleRemoveService(service) {
    setServices(services.filter(s => s !== service));
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        // Supabase Storage zwraca pełny URL, nie dodajemy BACKEND_URL
        setLogoUrl(data.url);
        toast.success('Logo dodane!');
      } else {
        toast.error('Nie udało się przesłać logo');
      }
    } catch (error) {
      toast.error('Błąd przesyłania');
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        // Supabase Storage zwraca pełny URL, nie dodajemy BACKEND_URL
        setGalleryImages([...galleryImages, data.url]);
        toast.success('Zdjęcie dodane!');
      } else {
        toast.error('Nie udało się przesłać zdjęcia');
      }
    } catch (error) {
      toast.error('Błąd przesyłania');
    } finally {
      setUploading(false);
    }
  }

  function handleRemoveImage(index) {
    setGalleryImages(galleryImages.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/my-company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          name, description, location, phone, website, nip, regon, address, 
          invoice_email: invoiceEmail,
          company_name: companyName,
          services, 
          gallery_images: galleryImages,
          logo_url: logoUrl
        }),
      });
      if (res.ok) {
        toast.success('Profil zaktualizowany!');
      } else {
        toast.error('Nie udało się zapisać');
      }
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-profile-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          <h1 className="font-heading font-bold text-2xl text-white">Profil firmy</h1>
          <p className="text-slate-400 mt-1">Uzupełnij dane swojej firmy</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Logo Section */}
        <Card className="border-2 border-orange-200">
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Building2 className="w-5 h-5" />Logo firmy</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">Logo będzie widoczne na liście firm i w Twoim profilu. Zalecany rozmiar: 200x200px.</p>
            
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo firmy" className="w-24 h-24 object-cover" />
                ) : (
                  <Building2 className="w-10 h-10 text-slate-300" />
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={() => logoInputRef.current?.click()} 
                  disabled={uploadingLogo}
                  variant="outline"
                  className="border-orange-500 text-orange-500 hover:bg-orange-50"
                >
                  {uploadingLogo ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Przesyłanie...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" />{logoUrl ? 'Zmień logo' : 'Dodaj logo'}</>
                  )}
                </Button>
                {logoUrl && (
                  <Button variant="ghost" size="sm" onClick={() => setLogoUrl('')} className="text-red-500">
                    <X className="w-4 h-4 mr-1" />Usuń logo
                  </Button>
                )}
              </div>
            </div>
            
            <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading">Podstawowe informacje</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nazwa firmy</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label>Opis firmy</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} placeholder="Opisz działalność, doświadczenie, specjalizacje..." />
            </div>
            <div>
              <Label>Usługi</Label>
              <div className="flex gap-2 mb-2">
                <Input value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Dodaj usługę..." onKeyPress={(e) => e.key === 'Enter' && handleAddService()} />
                <Button type="button" onClick={handleAddService} variant="outline">Dodaj</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {services.map((service, idx) => (
                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                    {service}<button onClick={() => handleRemoveService(service)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading">Dane firmy (do faktur)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-4">
              <p className="text-sm text-orange-800">
                <strong>Ważne:</strong> Te dane zostaną użyte do wystawiania faktur. Upewnij się, że są poprawne.
              </p>
            </div>
            <div>
              <Label>Pełna nazwa firmy (do faktury)</Label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="np. Firma Budowlana XYZ Sp. z o.o." />
              <p className="text-xs text-slate-500 mt-1">Nazwa firmy jaka ma widnieć na fakturze</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NIP *</Label>
                <Input value={nip} onChange={(e) => setNip(e.target.value)} placeholder="1234567890" />
              </div>
              <div>
                <Label>REGON</Label>
                <Input value={regon} onChange={(e) => setRegon(e.target.value)} placeholder="123456789" />
              </div>
            </div>
            <div>
              <Label>Adres siedziby *</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="ul. Przykładowa 1, 00-000 Miasto" />
              <p className="text-xs text-slate-500 mt-1">Pełny adres wraz z kodem pocztowym i miastem</p>
            </div>
            <div>
              <Label>Email do faktur *</Label>
              <Input type="email" value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} placeholder="faktury@twojafirma.pl" />
              <p className="text-xs text-slate-500 mt-1">Na ten adres będą wysyłane faktury</p>
            </div>
            <div>
              <Label>Lokalizacja działania</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="np. Warszawa i okolice" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading">Kontakt</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefon</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+48 123 456 789" />
              </div>
              <div>
                <Label>Strona WWW</Label>
                <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://twojafirma.pl" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Image className="w-5 h-5" />Galeria zdjęć</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">Dodaj zdjęcia swoich realizacji, aby pokazać klientom jakość wykonanych prac.</p>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              {galleryImages.map((img, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img src={img} alt={`Zdjęcie ${idx + 1}`} className="w-full h-full object-cover" />
                  <button onClick={() => handleRemoveImage(idx)} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="aspect-square border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-500 hover:border-orange-500 hover:text-orange-500 transition-colors">
                {uploading ? <Loader2 className="w-8 h-8 animate-spin" /> : <><Upload className="w-8 h-8 mb-2" /><span className="text-sm">Dodaj zdjęcie</span></>}
              </button>
            </div>
            
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Zapisywanie...</> : 'Zapisz zmiany'}
          </Button>
        </div>
      </div>
    </div>
  );
}
